'use client';

import React from 'react';
import styles from './ContestStatusBox.module.scss';
import Image from 'next/image';
import { useTranslation } from '@app/i18n/useTranslationClient';
import { Button, ButtonColor, ButtonType } from '@/components/button/Button';
import { CountdownClock } from '@/components/countdownClock/CountdownClock';
import { ShowOnDesktopOnly } from '@/components/show-on/ShowOn';
import { scrollToSection } from '@/utils/utils';

interface IContestStatusBox {
    resolved: boolean;
    endDate: string;
    isContestOpen: boolean;
    contestTitle: string;
    contestStatus: string;
    contestId: string;
    isAuthenticated?: boolean;
}

const authenticatedOffset = 20;
const unauthenticatedOffset = 30;

export const ContestStatusBox: React.FC<IContestStatusBox> = ({
    isContestOpen,
    resolved,
    contestTitle,
    contestStatus,
    endDate,
    contestId,
    isAuthenticated,
}) => {
    const { t } = useTranslation();

    const hasButton = (!resolved && isContestOpen) || (resolved && !isContestOpen);
    const scrollOffsetTop = isAuthenticated ? authenticatedOffset : unauthenticatedOffset;

    const buttonTitle = resolved
        ? t('translations.account.contests.showWinners')
        : t('translations.account.contests.join');

    const handleOnclick = () => scrollToSection(contestId, scrollOffsetTop);

    return (
        <div className={styles.contestBox}>
            <ShowOnDesktopOnly>
                <div className={styles.contestBoxHeader}>{contestTitle}</div>
            </ShowOnDesktopOnly>
            <div className={styles.contestBoxContent}>
                <p className={styles.contestBoxTitle}>{t('translations.account.contests.applicationDeadline')}</p>
                <p className={styles.contestBoxStatus}>{contestStatus}</p>
            </div>
            {isContestOpen && <CountdownClock data-testid={'countdownClock'} endDate={endDate} />}
            {hasButton ? (
                <Button
                    data-testid={'statusBoxButton'}
                    buttonType={ButtonType.BUTTON}
                    buttonColor={ButtonColor.BLACK}
                    className={styles.contestBoxButton}
                    onClick={handleOnclick}>
                    {buttonTitle}
                </Button>
            ) : (
                <div className={styles.contestBoxInfo}>
                    <Image src={'/icons/icon-info.svg'} alt={'info-icon'} width={20} height={20} />
                    <p className={styles.contestBoxInfoText}>{t('translations.contest.status.unresolvedInfo')}</p>
                </div>
            )}
        </div>
    );
};
